// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import xIconsObject from "@west/XDL/Icons/xngCommonIcons";
import { openHubSpotLiveChatHandler } from "@west/XDL/Utilities/liveChat";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { shallowEqual } from "react-redux";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    title = "title",
    titleLink = "titleLink",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    horizontalWrapper?: InjectableCss;
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    titleLink?: LinkPresentationProps;
    linksWrapper?: InjectableCss;
    horizontalLinksWrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    itemLink?: LinkPresentationProps;
}

export const linkListStyles: LinkListStyles = {};

const styles = linkListStyles;
export const FooterLinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { xGoogleAnalytics } = useAppSelector(state => {
        return {
            xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        };
    }, shallowEqual);
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);

    // XNG-Changes
    const footerLinkClickHandler = (url?: string, footerLinkLabel?: string) => {
        if (xGoogleAnalytics.footerLinksClickEvent && url) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.footerLinksClickEventName,
                click_text: footerLinkLabel,
                destination: url,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    if (fields.direction === "horizontal") {
        return (
            <StyledWrapper {...styles.horizontalWrapper}>
                {links.map((link, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper key={index} {...styles.horizontalLinksWrapper}>
                        {(link.url || (fields.links[index].fields as any).isLiveChat) && (
                            <Link
                                className="list-link"
                                {...styles.itemLink}
                                href={(fields.links[index].fields as any).isLiveChat ? undefined : link.url}
                                onClick={() =>
                                    (fields.links[index].fields as any).isLiveChat
                                        ? openHubSpotLiveChatHandler
                                        : footerLinkClickHandler(
                                              link.url,
                                              fields.links[index].fields.overriddenTitle || link.title,
                                          )
                                }
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                {fields.links[index].fields.overriddenTitle || link.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.linkListWrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                {titleLink ? (
                    <Link
                        className="list-title"
                        href={titleLink.url}
                        onClick={() => footerLinkClickHandler(titleLink.url, fields.title || titleLink.title)}
                        {...styles.titleLink}
                    >
                        {fields.title || titleLink.title}
                    </Link>
                ) : (
                    <Typography
                        className="list-title"
                        as="h3"
                        {...styles.titleLink?.typographyProps}
                        color={styles.titleLink?.color}
                    >
                        {fields.title}
                    </Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.linksWrapper}>
                {links.map((link, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper key={index} {...styles.itemWrapper}>
                        {(link.url || (fields.links[index].fields as any).isLiveChat) && (
                            <Link
                                className="list-link"
                                {...styles.itemLink}
                                href={(fields.links[index].fields as any).isLiveChat ? undefined : link.url}
                                onClick={() =>
                                    (fields.links[index].fields as any).isLiveChat
                                        ? openHubSpotLiveChatHandler
                                        : footerLinkClickHandler(
                                              link.url,
                                              fields.links[index].fields.overriddenTitle || link.title,
                                          )
                                }
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                {...{
                                    icon: {
                                        iconProps: {
                                            src: xIconsObject[(fields.links[index].fields as any)["icon"]] || undefined,
                                            size: 14,
                                            color: "common.ui4",
                                            css: css`
                                                width: ${(fields.links[index].fields as any).iconWidth || 14}px;
                                                height: ${(fields.links[index].fields as any).iconHeight || 14}px;
                                                svg {
                                                    width: ${(fields.links[index].fields as any).iconWidth || 14}px;
                                                    height: ${(fields.links[index].fields as any).iconHeight || 14}px;
                                                }
                                            `,
                                        },
                                    },
                                }}
                            >
                                {fields.links[index].fields.overriddenTitle ||
                                    link.title ||
                                    ((fields.links[index].fields as any).isLiveChat ? translate("Live Chat") : "")}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterLinkList,
    definition: {
        group: "Footer",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.direction !== "horizontal",
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isVisible: item => item.fields.direction !== "horizontal",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                        isLiveChat,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (isLiveChat) {
                        return translate("Live Chat");
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        isVisible: item => !item.fields.isLiveChat,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "isLiveChat",
                        displayName: "Live Chat",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "icon",
                        editorTemplate: "DropDownField",
                        options: Object.keys(xIconsObject).map(iconName => ({
                            displayName: iconName,
                            value: iconName,
                        })),
                        defaultValue: "",
                    },
                    {
                        name: "iconWidth",
                        displayName: "icon width",
                        editorTemplate: "IntegerField",
                        defaultValue: 14,
                        max: 40,
                        min: 0,
                    },
                    {
                        name: "iconHeight",
                        displayName: "icon height",
                        editorTemplate: "IntegerField",
                        defaultValue: 14,
                        max: 40,
                        min: 0,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
